import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useBusiness } from './BusinessContext';

/**
 * Context-aware business scope. When a user opens a linked entity (commitment,
 * task, call, payment, reading) that belongs to a specific business, we can
 * temporarily "focus" the app to that business without mutating the persistent
 * active-business selection. Press `Esc` to revert.
 *
 * Consumers read the *effective* business via `useEffectiveBusiness()`. The
 * regular `useBusiness()` still exposes the persistent selection.
 */
const BusinessScopeContext = createContext(null);

export function BusinessScopeProvider({ children }) {
  const { activeBusiness, businesses } = useBusiness();
  const [scopeId, setScopeId] = useState(null);

  const scopeBusiness = useMemo(() => {
    if (!scopeId) return null;
    return businesses.find((b) => b.id === scopeId) || null;
  }, [scopeId, businesses]);

  const enterScope = useCallback((businessId) => {
    if (!businessId) return;
    // Only scope if different from the already-active business
    if (activeBusiness?.id === businessId) return;
    setScopeId(businessId);
  }, [activeBusiness]);

  const exitScope = useCallback(() => setScopeId(null), []);

  // Esc exits scope (only when scope is active, don't interfere otherwise)
  useEffect(() => {
    if (!scopeId) return;
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      exitScope();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [scopeId, exitScope]);

  const value = useMemo(() => ({
    scopeBusiness,
    effectiveBusiness: scopeBusiness || activeBusiness,
    enterScope,
    exitScope,
  }), [scopeBusiness, activeBusiness, enterScope, exitScope]);

  return <BusinessScopeContext.Provider value={value}>{children}</BusinessScopeContext.Provider>;
}

export function useBusinessScope() {
  const ctx = useContext(BusinessScopeContext);
  if (!ctx) throw new Error('useBusinessScope must be used inside BusinessScopeProvider');
  return ctx;
}

export function useEffectiveBusiness() {
  return useBusinessScope().effectiveBusiness;
}
